import { useMemo, useState } from 'react'
import { User } from '@/types/users'
import { AverageMetricReport } from '@/types/workflow-metric-reports'
import { useGetUsers } from '@/service-library/hooks/users'
import { useGetMetricReport } from '@/service-library/hooks/metrics'
import ListAutocomplete from '@/components/list-autocomplete/ListAutocomplete'
import { useRootOrganization } from '@/components/organizations/RootOrganizationProvider'
import AverageMetricBarChart from '@/components/project-metrics-dashboard/AverageMetricBarChart'
import ChartCard from '@/components/project-metrics-dashboard/ChartCard'
import { CommonQueryOptions } from '@/components/project-metrics-dashboard/types'

type AverageHumanInteractionCardProps = {
  commonQueryOptions: CommonQueryOptions
}

export default function AverageHumanInteractionCard({
  commonQueryOptions,
}: AverageHumanInteractionCardProps) {
  const { rootOrganization } = useRootOrganization()
  const [user, setUser] = useState<User | { id: string; name: string }>({
    id: 'no-user-id',
    name: 'All',
  })
  const { users, isPending } = useGetUsers({
    filters: {
      limit: '1000',
      owner_org_id: rootOrganization.id,
    },
    enabled: !!rootOrganization,
  })

  const { report: userInteractionAverageReport } =
    useGetMetricReport<AverageMetricReport>({
      ...commonQueryOptions,
      reportKey: 'user-interaction-average',
      filters: {
        ...commonQueryOptions.filters,
        user_id: user.id === 'no-user-id' ? undefined : user.id,
      },
    })

  const userOptions = useMemo(
    () => [
      {
        id: 'no-user-id',
        name: 'All',
      },
      ...users,
    ],
    [users],
  )

  return (
    <ChartCard
      heading="Average Human Interaction"
      headingTooltip={
        <>
          The average time a human spends interacting with a document in this
          workflow state.
          <br />
          <br />
          This does not include time spent with the document open in an idle
          tab. This only includes time actively spent interacting with the
          document.
        </>
      }
      controls={
        <ListAutocomplete
          autoSelect={false}
          selected={user}
          setSelected={setUser}
          label="User"
          options={userOptions}
          loading={isPending}
          variant="outlined"
          sx={{ maxWidth: 250 }}
        />
      }
    >
      <AverageMetricBarChart
        report={userInteractionAverageReport}
        type="user-interaction-average"
      />
    </ChartCard>
  )
}
